import { createContext, useState, useContext, useEffect, useCallback } from "react";
import PropTypes from "prop-types";

const TrackingViewsContext = createContext(null);

const STORAGE_KEY = "contraspetrols_tracking_views";

/**
 * TrackingViewsProvider
 * 
 * Contexto que persiste las configuraciones de los paneles de tracking en localStorage.
 * Guarda información de operación, ruta, y configuraciones de cada panel.
 */
export const TrackingViewsProvider = ({ children }) => {
  // ==================== TRACKING VIEWS STATE ====================
  const [trackingViews, setTrackingViews] = useState(() => {
    // Inicializar desde localStorage
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        // Validar que sea un array
        if (Array.isArray(parsed)) {
          return parsed;
        }
      }
    } catch (error) {
      console.error("Error al cargar vistas de tracking desde localStorage:", error);
    }
    return [];
  });

  // ==================== SYNC TO LOCALSTORAGE ====================
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(trackingViews));
    } catch (error) {
      console.error("Error al guardar vistas de tracking en localStorage:", error);
    }
  }, [trackingViews]);

  // ==================== TRACKING VIEWS ACTIONS ====================
  
  /**
   * Agregar una nueva vista de tracking
   * @param {Object} viewData - Datos de la vista (operationId, routeId, operationName, routeName)
   */
  const addTrackingView = useCallback((viewData) => {
    setTrackingViews((prev) => {
      // Verificar si ya existe esta operacion
      const exists = prev.some(
        (v) => v.operationName === viewData.operationName
      );
      
      if (exists) {
        console.warn("Esta vista de tracking ya existe");
        return prev;
      }
      
      // Generar ID unico si no viene
      const newView = {
        ...viewData,
        id: viewData.id || `panel_${(viewData.operationName || 'op').replaceAll(/\s+/g, '_')}_${Date.now()}`,
        addedAt: new Date().toISOString(),
      };
      
      return [...prev, newView];
    });
  }, []);

  /**
   * Remover una vista de tracking por ID
   * @param {string} panelId - ID del panel a remover
   */
  const removeTrackingView = useCallback((panelId) => {
    setTrackingViews((prev) => prev.filter((v) => v.id !== panelId));
  }, []);

  /**
   * Limpiar todas las vistas de tracking
   */
  const clearTrackingViews = useCallback(() => {
    setTrackingViews([]);
  }, []);

  /**
   * Actualizar una vista existente (útil para cambiar configuraciones)
   * @param {string} panelId - ID del panel a actualizar
   * @param {Object} updates - Objeto con los cambios
   */
  const updateTrackingView = useCallback((panelId, updates) => {
    setTrackingViews((prev) =>
      prev.map((view) =>
        view.id === panelId
          ? { ...view, ...updates, updatedAt: new Date().toISOString() }
          : view
      )
    );
  }, []);

  /**
   * Verificar si una combinación operación-ruta ya existe
   * @param {string} operationId 
   * @param {string|null} routeId 
   * @returns {boolean}
   */
  const viewExists = useCallback((operationName) => {
    return trackingViews.some(
      (v) => v.operationName === operationName
    );
  }, [trackingViews]);

  /**
   * Obtener estadísticas de las vistas
   */
  const getStats = useCallback(() => {
    return {
      total: trackingViews.length,
      operations: new Set(trackingViews.map((v) => v.operationId)).size,
      routes: new Set(trackingViews.filter((v) => v.routeId).map((v) => v.routeId)).size,
    };
  }, [trackingViews]);

  const value = {
    // Estado
    trackingViews,
    
    // Acciones
    addTrackingView,
    removeTrackingView,
    clearTrackingViews,
    updateTrackingView,
    
    // Utilidades
    viewExists,
    getStats,
  };

  return (
    <TrackingViewsContext.Provider value={value}>
      {children}
    </TrackingViewsContext.Provider>
  );
};

/**
 * Hook para usar el contexto de vistas de tracking
 * @returns {Object} Contexto con trackingViews y funciones de gestión
 */
export const useTrackingViews = () => {
  const context = useContext(TrackingViewsContext);
  if (!context) {
    throw new Error("useTrackingViews debe ser usado dentro de TrackingViewsProvider");
  }
  return context;
};

TrackingViewsProvider.propTypes = {
  children: PropTypes.node.isRequired,
};
